'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carouselNew'
import { Chips } from '@pw-tech/omni-ui'
import cn from 'clsx'
import Head from 'next/head'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { CarouselProps } from './schema'

const CarouselWrapper = ({
  isFullWidth,
  isOuterLayer,
  isHover,
  smallBtns,
  turnOffSideArrows,
  isBtns,
  isLoop,
  isAutoplay,
  items,
  title,
  subTitle,
  addChips,
  isCuriousJr,
  circularArrows,
  homePageVariant,
  largeSideArrows,
  pageData,
}: CarouselProps) => {
  const [chipSelected, setChipSelected] = useState<number | null>(null)
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  const deviceId =
    typeof window !== 'undefined' ? window?.PWWebSDK?.randomId || localStorage.getItem('randomId') || '' : ''

  const userId = typeof window !== 'undefined' ? window?.PWWebSDK?.user?.id || '' : ''

  const renderResponsivePicture = ({
    desktopSrc,
    mobileSrc,
    alt,
    eager = false,
  }: {
    desktopSrc?: string
    mobileSrc?: string
    alt?: string
    eager?: boolean
  }) => {
    const loading = eager ? 'eager' : 'lazy'
    const fetchPriority = eager ? 'high' : 'auto'

    return (
      <picture className='w-full'>
        <source media='(max-width: 768px)' type='image/webp' srcSet={mobileSrc} />
        <source media='(min-width: 769px)' type='image/webp' srcSet={desktopSrc} />
        <img
          src={desktopSrc}
          alt={alt || 'Banner'}
          className='w-full object-cover'
          loading={loading}
          fetchPriority={fetchPriority}
        />
      </picture>
    )
  }

  const firstItem = items[0]

  return (
    <>
      {/* 🔼 Preload First Image */}
      <Head>
        <link
          rel='preload'
          as='image'
          href={firstItem?.imageUrl}
          imageSrcSet={`${firstItem?.mobileImageUrl} 768w, ${firstItem?.imageUrl} 1200w`}
          media='(min-width: 320px)'
          fetchPriority='high'
        />
      </Head>

      <div>
        {addChips && (
          <div className='flex overflow-x-scroll gap-3.5 scrollbar-hide my-4'>
            {items.map((item, index) =>
              item?.chipName ? (
                <Chips
                  key={index}
                  focused={chipSelected === index}
                  onClick={() => {
                    setChipSelected(chipSelected === index ? null : index)
                  }}
                >
                  {item?.chipName}
                </Chips>
              ) : null,
            )}
          </div>
        )}

        <div
          className={cn({
            'bg-white shadow-xl p-6 pb-8 rounded-lg': isOuterLayer,
          })}
          style={{
            boxShadow: isOuterLayer ? '0px 0px 5px #a3a3a389' : '',
          }}
        >
          {!hydrated ? (
            <div>
              <Link href={firstItem?.link || '#'}>
                {renderResponsivePicture({
                  desktopSrc: firstItem?.imageUrl,
                  mobileSrc: firstItem?.mobileImageUrl,
                  alt: firstItem?.alt,
                  eager: true,
                })}
              </Link>
            </div>
          ) : (
            <Carousel
              opts={{
                align: 'start',
                loop: isLoop,
              }}
              className='w-full relative'
              autoplayInterval={isAutoplay ? 5000 : 0}
              showDots={isBtns}
              homePageVariant={homePageVariant}
            >
              <CarouselContent
                defaultDotsBg='#D9DCE1'
                activeDotsBg='#414347'
                smallDots={smallBtns}
                isHover={isHover}
                className='flex gap-4'
                homePageVariant={homePageVariant}
              >
                {chipSelected === null ? (
                  items?.map(({ imageUrl, link, mobileImageUrl, alt }, index) => (
                    <CarouselItem key={index} homePageVariant={homePageVariant}>
                      <Link
                        href={link || '#'}
                      >
                        {renderResponsivePicture({
                          desktopSrc: imageUrl,
                          mobileSrc: mobileImageUrl,
                          alt,
                          eager: index === 0,
                        })}
                      </Link>
                    </CarouselItem>
                  ))
                ) : (
                  <CarouselItem homePageVariant={homePageVariant}>
                    <Link
                      href={items[chipSelected]?.link || '#'}
                    >
                      <img
                        alt='ChipSelectedImage'
                        src={items[chipSelected]?.imageUrl}
                        className={`w-full hidden md:block object-cover ${!isCuriousJr ? 'aspect-[16/9]' : ''}`}
                      />
                      <img
                        alt='ChipSelectedImageMweb'
                        src={items[chipSelected]?.mobileImageUrl}
                        className={`w-full md:hidden object-cover ${!isCuriousJr ? 'aspect-[16/9]' : ''}`}
                      />
                    </Link>
                  </CarouselItem>
                )}
              </CarouselContent>

              {!turnOffSideArrows && (
                <CarouselPrevious
                  isStretched={circularArrows}
                  largeSideArrows={largeSideArrows}
                  className='hidden md:flex'
                />
              )}
              {!turnOffSideArrows && (
                <CarouselNext
                  isStretched={circularArrows}
                  largeSideArrows={largeSideArrows}
                  className='hidden md:flex'
                />
              )}
            </Carousel>
          )}
        </div>
      </div>
    </>
  )
}

export default CarouselWrapper
